import React, { useState, useEffect, useCallback, useRef } from 'react';
import CopilotWidget from '../components/CopilotWidget';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import TruckMap from '../components/TruckMap';
import {
  routeAiMovementDecision,
  routeAiReplanThresholdMeters,
  type RouteAiOriginSnapshot,
} from '../utils/routeAiReplan';
import DeliveryConfirmation from '../components/DeliveryConfirmation';
import { queueGpsPing, syncGpsQueue, setupOfflineSync, isOnline, getQueueCount } from '../services/offlineGps';
import { replaceReferenceDataset, loadReferenceDataset } from '../offline/referenceData';
import { loadWorkflowCheckpoint, saveWorkflowCheckpoint } from '../offline/workflows';
import {
  BrandMark, Card, SectionHeader, MetricTile, StatusPill, SmartStatus,
  Spinner, EmptyState, ProgressBar, SegmentedTabs, IconButton, Alert, Avatar,
  Pagination, usePagination,
} from '../components/ui';
import { evaluateGpsOdometerMovement } from '../utils/gpsOdometerPolicy';

interface TruckInfo {
  id: string;
  registration_number: string;
  capacity_kg: number;
  status: string;
  current_lat: number | null;
  current_lng: number | null;
  fuel_level_pct: number;
  odometer_km: number;
  last_maintenance_at: string | null;
  next_maintenance_odometer: number | null;
  driver_name: string | null;
  driver_phone: string | null;
  last_updated?: any;
}

interface DispatchItem {
  id: string;
  batch_id: string;
  truck_id: string;
  driver_id: string;
  status: string;
  assigned_at: string;
  actual_departure: string | null;
  delivered_at: string | null;
  notes: string | null;
  batch_farmer_name: string;
  batch_farmer_phone: string;
  batch_farmer_village: string;
  batch_est_weight: number | null;
  batch_actual_weight: number | null;
  batch_quality_grade: string | null;
  batch_gps_lat: number | null;
  batch_gps_lng: number | null;
  batch_status: string;
  batch_calculated_amount: number | null;
  truck_registration: string;
  truck_capacity_kg: number;
  truck_fuel_level: number;
  delivery_confirmation_id: string | null;
  delivery_confirmation_status: string | null;
  delivery_confirmed_weight: number | null;
  delivery_photo_url: string | null;
}

interface DriverStats {
  total_deliveries: number;
  deliveries_today: number;
  deliveries_this_week: number;
  total_distance_km: number;
  on_time_rate: number;
  avg_delivery_time_mins: number;
}

interface MaintenanceLog {
  id: string;
  maintenance_type: string;
  description: string | null;
  cost: number;
  odometer_km: number | null;
  performed_by: string | null;
  performed_at: string;
  next_maintenance_km: number | null;
}

// ─── Inline SVG icon set (geometric, enterprise — no cartoony outlines) ───
const Icon = {
  Package: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  ),
  MapPin: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Fuel: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 22V4a2 2 0 012-2h6a2 2 0 012 2v18M3 14h10M14 8h2a2 2 0 012 2v6a2 2 0 002 2 2 2 0 002-2V9.5L17 5" />
    </svg>
  ),
  Gauge: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 14l4-4M3.34 19a10 10 0 1117.32 0" />
    </svg>
  ),
  Clock: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Refresh: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M23 4v6h-6M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  ),
  Navigation: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 11l19-9-9 19-2-8-8-2z" />
    </svg>
  ),
  Calendar: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Phone: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  Weight: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M6.5 6.5h11l1.4 13a1 1 0 01-1 1.5H6.1a1 1 0 01-1-1.5l1.4-13z" />
      <path d="M9 6.5a3 3 0 016 0" />
    </svg>
  ),
  Award: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="8" r="6" />
      <path d="M15.5 13.5L17 22l-5-3-5 3 1.5-8.5" />
    </svg>
  ),
  Route: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 000-7h-11a3.5 3.5 0 010-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  ),
  TrendingUp: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M23 6l-9.5 9.5-5-5L1 18" />
      <path d="M17 6h6v6" />
    </svg>
  ),
  Activity: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  Wrench: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14.7 6.3a4 4 0 01-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 015.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.6z" />
    </svg>
  ),
  Send: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  ),
  Check: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  X: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  AlertTriangle: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />
    </svg>
  ),
  ChevronRight: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  ),
  ClipboardCheck: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  Building: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 21h18M5 21V5a2 2 0 012-2h10a2 2 0 012 2v16M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
    </svg>
  ),
  Zap: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
};

const TruckPortal: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'dispatches' | 'bags' | 'map' | 'stats' | 'maintenance' | 'route-ai'>('dispatches'); // v3.2: added 'route-ai'
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [truck, setTruck] = useState<TruckInfo | null>(null);
  const [dispatches, setDispatches] = useState<DispatchItem[]>([]);
  const [stats, setStats] = useState<DriverStats | null>(null);
  const [maintenanceLogs, setMaintenanceLogs] = useState<MaintenanceLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<DispatchItem | null>(null);

  const gpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gpsWatchRef = useRef<number | null>(null);
  const lastGpsPosRef = useRef<{
    lat: number;
    lng: number;
    accuracyM: number | null;
    odometer: number;
  } | null>(null);

  // Long-lived Geolocation callbacks must never depend on render-captured
  // dispatch arrays. Keep the current GPS-eligible dispatch in a live ref.
  const activeGpsDispatchRef = useRef<DispatchItem | null>(null);
  const [gpsTracking, setGpsTracking] = useState(false);
  // Live position is restored only from the authenticated user's device-scoped
  // workflow checkpoint; precise location is never kept in generic localStorage.
  const [liveGpsPos, setLiveGpsPos] = useState<{ lat: number; lng: number } | null>(null);
  const [lastGpsUpdate, setLastGpsUpdate] = useState<Date | null>(null);
  // v3.11.19: GPS accuracy in meters (shown in UI, used for filtering).
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [onlineStatus, setOnlineStatus] = useState(isOnline());
  const [pendingPings, setPendingPings] = useState(0);
  const refreshPendingPings = useCallback(() => { if (user?.id) void getQueueCount(user.id).then(setPendingPings).catch(() => {}); }, [user?.id]);
  const [sessionDistance, setSessionDistance] = useState(0);
  const [routeSummaries, setRouteSummaries] = useState<Record<string, { mode: 'road' | 'direct'; distanceKm: number; durationSeconds: number | null; source: string }>>({});

  useEffect(() => {
    refreshPendingPings();
  }, [refreshPendingPings]);

  // Keep the GPS watcher bound to the latest authoritative/cached dispatch
  // state without restarting navigator.geolocation.watchPosition on every
  // dispatch refresh.
  useEffect(() => {
    activeGpsDispatchRef.current =
      dispatches.find(
        d => d.status === 'IN_TRANSIT' || d.status === 'DISPATCHED'
      ) || null;
  }, [dispatches]);

  const [updatingDispatchId, setUpdatingDispatchId] = useState<string | null>(null);
  // v1.6.0: bag loading state
  const [bagLoadingDispatch, setBagLoadingDispatch] = useState<string | null>(null);
  const [bagCount, setBagCount] = useState('');
  const [bagWeights, setBagWeights] = useState<string[]>([]);
  const [bagLoading, setBagLoading] = useState(false);
  const [bagManifestLoading, setBagManifestLoading] = useState(false);
  const [bagResult, setBagResult] = useState<any | null>(null);

  // Mobile navigation safety: the enterprise header/telemetry stack is fixed and
  // #truck-main-content is the authoritative mobile scroll container. Reset that
  // container (not an element scrollIntoView target) whenever the Driver destination
  // changes so the first heading/GPS control can never remain hidden under the header.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isDriverMobile = window.innerWidth <= 768 || (window.innerWidth <= 900 && window.innerHeight <= 500);
    if (!isDriverMobile) return;
    const main = document.getElementById('truck-main-content');
    if (main) main.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeTab]);

  // The enterprise drawer is owned by enterprise-layout.js. Observe its body class
  // so the React bottom-navigation exposes the real open/closed state without
  // duplicating drawer state or changing navigation business logic.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const sync = () => setMobileDrawerOpen(document.body.classList.contains('ed-mobile-drawer-is-open'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [truckRes, dispatchesRes, statsRes] = await Promise.all([
        apiClient.get('/truck-portal/me').catch(() => ({ data: null })),
        apiClient.get('/truck-portal/dispatches').catch(() => ({ data: null })),
        apiClient.get('/truck-portal/stats').catch(() => ({ data: { stats: null, truck: null } })),
      ]);

      let resolvedTruck: TruckInfo | null = truckRes.data?.truck || null;
      let resolvedDispatches: DispatchItem[] | null = Array.isArray(dispatchesRes.data?.dispatches) ? dispatchesRes.data.dispatches : null;
      if (user?.id && resolvedTruck) {
        try { await replaceReferenceDataset(user.id, 'driver_assigned_truck', [resolvedTruck], { ttlMs: 24 * 60 * 60 * 1000 }); } catch {}
      } else if (user?.id) {
        const cached = await loadReferenceDataset<TruckInfo>(user.id, 'driver_assigned_truck', true);
        resolvedTruck = cached.records[0] || null;
      }
      if (user?.id && resolvedDispatches) {
        try { await replaceReferenceDataset(user.id, 'driver_dispatches', resolvedDispatches, { ttlMs: 12 * 60 * 60 * 1000 }); } catch {}
      } else if (user?.id) {
        const cached = await loadReferenceDataset<DispatchItem>(user.id, 'driver_dispatches', true);
        resolvedDispatches = cached.records;
      }
      setTruck(resolvedTruck);
      setDispatches(resolvedDispatches || []);
      if (statsRes.data?.stats) setStats(statsRes.data.stats);
      if (!truckRes.data && (resolvedTruck || resolvedDispatches?.length)) setError('Offline mode: showing the last locally stored truck/dispatch assignment. New HQ changes will be reconciled when the server is reachable.');
    } catch (err: any) {
      setError('Failed to load online or locally cached Driver data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const startGpsTracking = () => {
    if (gpsWatchRef.current !== null) return;

    const activeDispatch = activeGpsDispatchRef.current;
    if (!activeDispatch?.id) {
      setError(
        'GPS trip tracking cannot start until a DISPATCHED or IN_TRANSIT assignment is available.'
      );
      return;
    }

    setGpsTracking(true);
    if (user?.id) void saveWorkflowCheckpoint(user.id, 'DRIVER_GPS', { state: 'ACTIVE', last_user_action: 'START_GPS', sync_state: 'LOCAL_ONLY' }).catch(() => {});
    setSessionDistance(0);

    const handlePosition = (pos: GeolocationPosition) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      // v3.11.19: Accuracy filtering — reject low-accuracy readings (likely IP-based, not real GPS).
      // Readings with accuracy > 100m are usually from WiFi/IP geolocation, not real GPS.
      const accuracy = pos.coords.accuracy || 9999;
      setGpsAccuracy(accuracy);
      if (accuracy > 100) {
        console.warn(`[GPS] Low accuracy reading (${accuracy.toFixed(0)}m) — rejecting. Waiting for real GPS fix.`);
        // v3.11.20: accuracy filter — don't update liveGpsPos, don't save to DB, don't update truck state.
        // The map will show 'Acquiring GPS...' overlay until we get a real fix.
        return;
      }
      const speedKmh = pos.coords.speed ? pos.coords.speed * 3.6 : null;
      const heading = pos.coords.heading;

      // PostgreSQL NUMERIC values may arrive through node-postgres as strings.
      // Normalize before arithmetic so GPS distance is numerically added rather
      // than string-concatenated (e.g. '0.0' + 0.01).
      const rawOdometer = Number(truck?.odometer_km ?? 0);
      let newOdometer = Number.isFinite(rawOdometer) && rawOdometer >= 0
        ? rawOdometer
        : 0;

      let distanceDelta = 0;
      const currentAccuracyM =
        Number.isFinite(Number(accuracy)) && Number(accuracy) > 0
          ? Number(accuracy)
          : null;

      if (lastGpsPosRef.current) {
        const movement = evaluateGpsOdometerMovement(
          {
            lat: lastGpsPosRef.current.lat,
            lng: lastGpsPosRef.current.lng,
            accuracyM: lastGpsPosRef.current.accuracyM,
          },
          {
            lat,
            lng,
            accuracyM: currentAccuracyM,
            speedKmh,
          }
        );

        if (movement.movementAccepted) {
          distanceDelta = movement.deltaKm;
          newOdometer = lastGpsPosRef.current.odometer + distanceDelta;
          setSessionDistance(prev => prev + distanceDelta);

          // Advance only the accepted odometer anchor. Rejected raw GPS fixes
          // remain visible/stored elsewhere but cannot walk the business odometer.
          lastGpsPosRef.current = {
            lat,
            lng,
            accuracyM: currentAccuracyM,
            odometer: newOdometer,
          };
        }
      } else {
        // First valid GPS fix establishes an odometer anchor without inventing
        // distance from an unknown pre-session path.
        lastGpsPosRef.current = {
          lat,
          lng,
          accuracyM: currentAccuracyM,
          odometer: newOdometer,
        };
      }

      // v3.11.11: Update the live GPS marker IMMEDIATELY — do not wait for server POST to return.
      setLiveGpsPos({ lat, lng });
      if (user?.id) void saveWorkflowCheckpoint(user.id, 'DRIVER_GPS', { state: 'ACTIVE', last_user_action: 'GPS_FIX', draft_data: { lastPos: { lat, lng }, accuracyM: accuracy, updatedAt: new Date().toISOString() }, sync_state: 'PENDING_SYNC' }).catch(() => {});

      // Resolve the dispatch from live state, not from the React render that
      // originally created this long-lived watchPosition callback.
      const activeDispatch = activeGpsDispatchRef.current;
      const dispatchId = activeDispatch?.id;

      // Trip telemetry without dispatch provenance is invalid evidence.
      // Never enqueue a command that the server is guaranteed to reject.
      if (!dispatchId) {
        setError(
          'GPS trip tracking is paused because there is no current DISPATCHED or IN_TRANSIT assignment.'
        );
        return;
      }

      const ping = {
        lat, lng,
        speedKmh: speedKmh ?? undefined,
        heading: heading ?? undefined,
        odometerKm: newOdometer,
        dispatchId,
        // Preserve the device observation time supplied by W3C Geolocation.
        timestamp: new Date(pos.timestamp).toISOString(),
        accuracyM: accuracy,
      };

      // Offline-first invariant: every accepted GPS ping is committed to IndexedDB
      // (GPS event + outbox + audit) before it is eligible for server synchronization.
      // Online connectivity only changes how quickly the already-durable command is ACKed.
      if (user?.id) {
        void queueGpsPing(ping, user.id).then(() => {
          setLastGpsUpdate(new Date());
          refreshPendingPings();
          if (isOnline()) requestAnimationFrame(() => { void syncGpsQueue(apiClient).then(refreshPendingPings); });
        }).catch((commitError: any) => {
          setError(`GPS could not be stored safely on this device: ${commitError?.message || 'local storage failure'}`);
        });
      } else {
        setError('GPS capture is blocked because the authenticated driver identity is unavailable.');
      }

      setTruck(prev => prev ? {
        ...prev,
        current_lat: lat,
        current_lng: lng,
        last_updated: new Date(),
        odometer_km: newOdometer,
      } : null);
    };

    if (navigator.geolocation) {
      gpsWatchRef.current = navigator.geolocation.watchPosition(
        handlePosition,
        (err) => { console.warn('GPS tracking error:', err.message); },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }  // v3.11.19: maximumAge: 0 forces fresh GPS readings (no cache)
      );
    }

    const cleanup = setupOfflineSync(apiClient, () => {
      refreshPendingPings();
      setLastGpsUpdate(new Date());
    });

    gpsIntervalRef.current = setInterval(() => {
      refreshPendingPings();
      setOnlineStatus(isOnline());
    }, 5000) as any;

    return cleanup;
  };

  const stopGpsTracking = () => {
    if (gpsWatchRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(gpsWatchRef.current);
      gpsWatchRef.current = null;
    }
    if (gpsIntervalRef.current) {
      clearInterval(gpsIntervalRef.current);
      gpsIntervalRef.current = null;
    }
    setGpsTracking(false);
    if (user?.id) void saveWorkflowCheckpoint(user.id, 'DRIVER_GPS', { state: 'PAUSED', last_user_action: 'STOP_GPS', sync_state: 'LOCAL_ONLY' }).catch(() => {});
  };

  // Recover the current authenticated driver's GPS workflow after reload or
  // browser process suspension. Another user on the same device receives a
  // different checkpoint key and cannot inherit this state.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void loadWorkflowCheckpoint<any>(user.id, 'DRIVER_GPS').then(checkpoint => {
      if (cancelled || !checkpoint) return;
      const lastPos = (checkpoint.draft_data as any)?.lastPos;
      if (lastPos && Number.isFinite(lastPos.lat) && Number.isFinite(lastPos.lng)) setLiveGpsPos(lastPos);
      if (checkpoint.state === 'ACTIVE' && gpsWatchRef.current === null) {
        window.setTimeout(() => { if (!cancelled && gpsWatchRef.current === null) startGpsTracking(); }, 100);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  // startGpsTracking intentionally omitted: this recovery runs only when user identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const updateDispatchStatus = async (dispatchId: string, status: string) => {
    setUpdatingDispatchId(dispatchId);
    try {
      await apiClient.patch(`/truck-portal/dispatches/${dispatchId}/status`, { status });
      fetchAll();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingDispatchId(null);
    }
  };

  const handleDeliveryConfirm = (dispatch: DispatchItem) => {
    setSelectedDispatch(dispatch);
    setShowConfirmModal(true);
  };

  // Authoritative Driver bag-loading workflow.
  // Physical custody may only be established while the dispatch is ASSIGNED.
  const handleLoadBags = async () => {
    if (!bagLoadingDispatch) return;

    const dispatch = dispatches.find(d => d.id === bagLoadingDispatch);

    if (!dispatch) {
      setError('Selected dispatch is no longer available');
      return;
    }

    if (dispatch.status !== 'ASSIGNED') {
      setError(`Bag loading is only available while the dispatch is ASSIGNED. Current status: ${dispatch.status}`);
      return;
    }

    const batchId = dispatch.batch_id;
    if (!batchId) {
      setError('No batch found for this dispatch');
      return;
    }

    // Once an authoritative persisted manifest exists it is immutable from
    // this Driver workflow. Backend duplicate protection remains authoritative.
    if (bagResult?.bags?.length > 0) {
      setError('This batch already has an authoritative bag manifest');
      return;
    }

    const weights = bagWeights.map(w => Number(w));

    if (
      weights.length === 0 ||
      weights.some(weight => !Number.isFinite(weight) || weight <= 0)
    ) {
      setError('Every bag must have a positive numeric measured weight');
      return;
    }

    setBagLoading(true);
    setError(null);

    try {
      const bags = weights.map(weight_kg => ({ weight_kg }));

      const res = await apiClient.post(
        `/chain/batches/${batchId}/bags`,
        { bags }
      );

      // POST response is already the authoritative committed manifest.
      setBagResult({
        ...res.data,
        existing: false,
        locked: true,
      });

      // Refresh dispatch/truck state without clearing the committed manifest.
      await fetchAll();

    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        'Failed to establish the bag manifest'
      );
    } finally {
      setBagLoading(false);
    }
  };

  const startBagLoading = async (dispatchId: string) => {
    setBagLoadingDispatch(dispatchId || null);
    setBagCount('');
    setBagWeights([]);
    setBagResult(null);
    setError(null);

    if (!dispatchId) return;

    const dispatch = dispatches.find(d => d.id === dispatchId);

    if (!dispatch) {
      setError('Selected dispatch is no longer available');
      return;
    }

    if (dispatch.status !== 'ASSIGNED') {
      setError('Only ASSIGNED dispatches may enter physical bag loading');
      return;
    }

    if (!dispatch.batch_id) {
      setError('Selected dispatch has no batch');
      return;
    }

    setBagManifestLoading(true);

    try {
      // Read the authoritative persisted manifest before permitting a write.
      const res = await apiClient.get(
        `/chain/batches/${dispatch.batch_id}/bags`
      );

      const persistedBags = Array.isArray(res.data?.bags)
        ? res.data.bags
        : [];

      if (persistedBags.length > 0) {
        const totalWeight = persistedBags.reduce(
          (sum: number, bag: any) =>
            sum + (Number(bag.weight_kg) || 0),
          0
        );

        setBagResult({
          bags: persistedBags,
          count: persistedBags.length,
          total_weight_kg: totalWeight,
          existing: true,
          locked: true,
        });

        setBagCount(String(persistedBags.length));
        setBagWeights(
          persistedBags.map((bag: any) =>
            String(bag.weight_kg ?? '')
          )
        );
      }

    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        'Failed to read the authoritative bag manifest'
      );
    } finally {
      setBagManifestLoading(false);
    }
  };

  const updateBagCount = (count: string) => {
    setBagCount(count);
    const n = parseInt(count) || 0;
    setBagWeights(Array.from({ length: n }, (_, i) => bagWeights[i] || ''));
  };

  // ─── Pagination hooks (MUST be called unconditionally — before any early return) ───
  const activeDispatches = dispatches.filter(d => d.status !== 'DELIVERED' && d.status !== 'CANCELLED');

  // Physical bag loading is a pre-departure custody operation.
  // Do not reuse the broader active-trip collection here.
  const bagLoadableDispatches = dispatches.filter(
    d => d.status === 'ASSIGNED' && Boolean(d.batch_id)
  );

  const completedDispatches = dispatches.filter(d => d.status === 'DELIVERED');
  const activePag = usePagination(activeDispatches, 10);
  const completedPag = usePagination(completedDispatches, 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="cargo" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading portal…
          </div>
        </div>
      </div>
    );
  }

  if (!truck) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center p-5 safe-top">
        <Card padding="lg" className="max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-warning-50 dark:bg-warning-500/10 flex items-center justify-center text-warning-600 dark:text-warning-500">
            <Icon.AlertTriangle className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">No truck assigned</h2>
          <p className="mt-1.5 text-sm text-ink-500 dark:text-ink-400 max-w-xs mx-auto">
            Your account isn't linked to a vehicle yet. Please contact your administrator to assign a truck.
          </p>
          <div className="mt-5 flex justify-center">
            <LogoutButton />
          </div>
        </Card>
      </div>
    );
  }

  const fuelTone = truck.fuel_level_pct < 20 ? 'danger' : truck.fuel_level_pct < 40 ? 'warning' : 'success';

  const tabs = [
    { id: 'dispatches',  label: 'Dispatches',  icon: <Icon.Package className="w-4 h-4" />,     badge: activeDispatches.length || undefined },
    // v1.6.0: bag loading — driver creates bag QR codes for each dispatch
    { id: 'bags',        label: 'Bag Loading', icon: <Icon.Package className="w-4 h-4" /> },
    { id: 'map',         label: 'Live Map',    icon: <Icon.MapPin className="w-4 h-4" /> },
    { id: 'stats',       label: 'Performance', icon: <Icon.TrendingUp className="w-4 h-4" /> },
    { id: 'maintenance', label: 'Maintenance', icon: <Icon.Wrench className="w-4 h-4" /> },
    { id: 'route-ai',    label: 'Route AI',    icon: <Icon.Zap className="w-4 h-4" /> }, // v3.2: LOGISTICS agent (Icon.Cpu not available in TruckPortal's Icon set)
  ];

  return (
    <div className="truck-dashboard-root min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* ─── Top brand bar ─── */}
      <header className="topbar sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="cargo" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{truck.registration_number}</p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{user?.name} · Driver</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {/* GPS tracking toggle */}
            <button
              onClick={gpsTracking ? stopGpsTracking : startGpsTracking}
              className={`hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                gpsTracking
                  ? onlineStatus
                    ? 'bg-success-500/10 text-success-700 dark:text-success-500'
                    : 'bg-warning-500/10 text-warning-700 dark:text-warning-500'
                  : 'bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700'
              }`}
            >
              {gpsTracking && onlineStatus && <span className="live-dot" />}
              <Icon.Navigation className="w-3.5 h-3.5" />
              <span>{gpsTracking ? (onlineStatus ? 'Live' : 'Offline') : 'GPS Off'}</span>
              {gpsTracking && lastGpsUpdate && (
                <span className="text-2xs text-ink-400 dark:text-ink-500 nums">
                  {lastGpsUpdate.toLocaleTimeString()}
                </span>
              )}
              {gpsTracking && pendingPings > 0 && (
                <span className="text-2xs bg-warning-500 text-white px-1.5 py-0.5 rounded-full nums">
                  {pendingPings} queued
                </span>
              )}
            </button>
            <IconButton label="Refresh" onClick={fetchAll} disabled={refreshing}>
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <ProfileButton accent="cargo" />
            <LogoutButton />
          </div>
        </div>

        {/* ─── Truck status strip ─── */}
        <div className="truck-status-strip max-w-7xl mx-auto px-4 md:px-6 pb-3 grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatusChip icon={<Icon.Gauge className="w-3.5 h-3.5" />} label="Status" value={truck.status} />
          <StatusChip
            icon={<Icon.Fuel className="w-3.5 h-3.5" />}
            label="Fuel"
            value={`${truck.fuel_level_pct}%`}
            tone={fuelTone === 'danger' ? 'danger' : fuelTone === 'warning' ? 'warning' : 'success'}
          />
          <StatusChip
            icon={<Icon.Route className="w-3.5 h-3.5" />}
            label="Odometer"
            value={`${Number(truck.odometer_km).toFixed(1)} km`}
            sub={gpsTracking && sessionDistance > 0 ? `+${sessionDistance.toFixed(2)} km trip` : undefined}
          />
          <StatusChip icon={<Icon.Package className="w-3.5 h-3.5" />} label="Active" value={`${activeDispatches.length} trips`} />
          <StatusChip icon={<Icon.Check className="w-3.5 h-3.5" />} label="Delivered" value={`${stats?.total_deliveries || 0}`} />
        </div>
      </header>

      {/* ─── Tabs (desktop) ─── */}
      <div className="truck-desktop-tabs hidden md:block max-w-7xl mx-auto px-6 pt-4">
        <SegmentedTabs items={tabs} active={activeTab} onChange={(id) => setActiveTab(id as any)} />
      </div>

      {/* ─── Content ─── */}
      <main id="truck-main-content" data-mobile-tab={activeTab} className="max-w-7xl mx-auto px-4 md:px-6 pt-4 md:pt-6 pb-6">
        {error && (
          <div className="mb-4">
            <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>
          </div>
        )}

        {/* Mobile GPS control: presentation-only wrapper around the existing start/stop handlers. */}
        <div className={`truck-mobile-gps md:hidden ${gpsTracking ? 'is-tracking' : 'is-off'}`} role="group" aria-label="Driver GPS tracking">
          <div className="truck-mobile-gps-state">
            <span className={`truck-mobile-gps-dot ${gpsTracking && liveGpsPos ? 'is-live' : ''}`} aria-hidden="true" />
            <Icon.Navigation className="w-4 h-4" />
            <span className="truck-mobile-gps-copy">
              <strong>GPS tracking</strong>
              <small className="nums">
                {!gpsTracking
                  ? 'Off'
                  : !onlineStatus
                    ? `Offline${pendingPings > 0 ? ` · ${pendingPings} queued` : ''}`
                    : liveGpsPos
                      ? `Live${gpsAccuracy != null ? ` · ±${Math.round(gpsAccuracy)}m` : ''}${lastGpsUpdate ? ` · ${lastGpsUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`
                      : 'Acquiring GPS'}
              </small>
            </span>
          </div>
          <button
            type="button"
            aria-pressed={gpsTracking}
            aria-label={gpsTracking ? 'Turn off GPS tracking' : 'Turn on GPS tracking'}
            onClick={gpsTracking ? stopGpsTracking : startGpsTracking}
            className="truck-mobile-gps-toggle"
          >
            {gpsTracking ? 'Stop' : 'Turn on'}
          </button>
        </div>

        {/* ─── DISPATCHES ─── */}
        {activeTab === 'dispatches' && (
          <div className="truck-tab-root truck-tab-dispatches space-y-6">
            <section>
              <SectionHeader
                eyebrow="In progress"
                title={`Active dispatches (${activePag.total})`}
                subtitle="Trips assigned to you that have not yet been completed."
                icon={<Icon.Activity className="w-4 h-4" />}
                className="truck-section-header mb-3"
              />

              <div className="truck-quick-actions md:hidden" aria-label="Driver quick actions">
                <button type="button" onClick={() => setActiveTab('map')}><Icon.MapPin className="w-4 h-4" /><span>Open map</span></button>
                <button type="button" onClick={() => setActiveTab('bags')}><Icon.ClipboardCheck className="w-4 h-4" /><span>Bag loading</span></button>
                <button type="button" onClick={() => setActiveTab('route-ai')}><Icon.Zap className="w-4 h-4" /><span>Route Assist</span></button>
                <button type="button" onClick={() => setActiveTab('maintenance')}><Icon.Wrench className="w-4 h-4" /><span>Maintenance</span></button>
              </div>

              {activePag.total === 0 ? (
                <Card padding="lg" className="truck-empty-state-card">
                  <EmptyState
                    icon={<Icon.Package className="w-5 h-5" />}
                    title="No active dispatches"
                    description="New assignments from your administrator will appear here."
                  />
                </Card>
              ) : (
                <>
                  <div className="space-y-3">
                    {activePag.paginatedItems.map(dispatch => (
                      <DispatchCard
                        key={dispatch.id}
                        dispatch={dispatch}
                        onStatusUpdate={updateDispatchStatus}
                        onConfirmDelivery={handleDeliveryConfirm}
                        updating={updatingDispatchId === dispatch.id}
                      />
                    ))}
                  </div>
                  {activePag.totalPages > 1 && (
                    <Card padding="none" className="mt-3">
                      <Pagination
                        page={activePag.page}
                        pageSize={activePag.pageSize}
                        total={activePag.total}
                        onPageChange={activePag.setPage}
                      />
                    </Card>
                  )}
                </>
              )}
            </section>

            {completedPag.total > 0 && (
              <section>
                <SectionHeader
                  eyebrow="History"
                  title={`Completed (${completedPag.total})`}
                  icon={<Icon.Check className="w-4 h-4" />}
                  className="mb-3"
                />
                <div className="space-y-3">
                  {completedPag.paginatedItems.map(dispatch => (
                    <DispatchCard
                      key={dispatch.id}
                      dispatch={dispatch}
                      onStatusUpdate={() => {}}
                      onConfirmDelivery={() => {}}
                      updating={false}
                    />
                  ))}
                </div>
                {completedPag.totalPages > 1 && (
                  <Card padding="none" className="mt-3">
                    <Pagination
                      page={completedPag.page}
                      pageSize={completedPag.pageSize}
                      total={completedPag.total}
                      onPageChange={completedPag.setPage}
                    />
                  </Card>
                )}
              </section>
            )}
          </div>
        )}

        {/* ─── v1.6.0: BAG LOADING ─── */}
        {activeTab === 'bags' && (
          <div className="truck-tab-root truck-tab-bags space-y-6">
            <Card padding="lg" className="truck-bag-workspace">
              <SectionHeader
                eyebrow="Bag loading"
                title="Load and tag bags"
                subtitle="Select a dispatch, capture bag weights, and generate the existing warehouse QR records."
                icon={<Icon.Package className="w-4 h-4" />}
                className="truck-section-header"
              />

              {/* Select dispatch */}
              <div className="mt-4 space-y-3">
                <div>
                  <label htmlFor="bag-dispatch" className="label">Select dispatch</label>
                  <select id="bag-dispatch" value={bagLoadingDispatch || ''}
                    onChange={(e) => startBagLoading(e.target.value)}
                    className="input">
                    <option value="">— Select a dispatch —</option>
                    {bagLoadableDispatches.map(d => (
                      <option key={d.id} value={d.id}>
                        {/* v2.1.1 (BUG#3 fix): server returns batch_farmer_name + batch_id (no farmer_name or dispatch_number).
                            Previously this always rendered "Unknown" because both fields were undefined. */}
                        #{d.id.slice(0, 8)} — {d.batch_farmer_name || 'Unknown'} ({d.batch_id ? 'Batch ready' : 'No batch'})
                      </option>
                    ))}
                  </select>
                </div>

                {bagLoadingDispatch && (
                  <>
                    <div>
                      <label htmlFor="bag-count" className="label">Number of bags</label>
                      <input id="bag-count" type="number" inputMode="numeric" min="1" max="200" value={bagCount}
                        onChange={(e) => updateBagCount(e.target.value)}
                        disabled={bagManifestLoading || Boolean(bagResult?.bags?.length)}
                        className="input" placeholder="e.g. 18" />
                    </div>

                    {bagWeights.length > 0 && (
                      <div>
                        <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-2">Bag weights (kg)</p>
                        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                          {bagWeights.map((w, i) => (
                            <div key={i}>
                              <label className="text-2xs text-ink-500">Bag {i + 1}</label>
                              <input type="number" inputMode="decimal" step="0.1" min="0.1" value={w}
                                onChange={(e) => {
                                  const newWeights = [...bagWeights];
                                  newWeights[i] = e.target.value;
                                  setBagWeights(newWeights);
                                }}
                                disabled={bagManifestLoading || Boolean(bagResult?.bags?.length)}
                                className="input text-sm" placeholder="62.5" />
                            </div>
                          ))}
                        </div>
                        <p className="text-2xs text-ink-500 mt-2">
                          Total: {bagWeights.reduce((sum, w) => sum + (parseFloat(w) || 0), 0).toFixed(1)} kg
                        </p>
                      </div>
                    )}

                    {bagManifestLoading && (
                      <div className="p-4 rounded-lg border border-ink-200 dark:border-ink-800">
                        <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
                          <Spinner size="sm" />
                          Verifying authoritative manifest…
                        </div>
                      </div>
                    )}

                    {bagResult?.bags?.length > 0 && (
                      <div className="p-4 bg-success-50 dark:bg-success-500/10 rounded-lg border border-success-500/20 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-success-700 dark:text-success-500">
                              Manifest locked
                            </p>
                            <p className="text-2xs text-ink-500 mt-1">
                              {bagResult.count} bags · {Number(bagResult.total_weight_kg).toFixed(2)} kg
                            </p>
                          </div>
                          <span className="text-2xs font-medium px-2 py-1 rounded-full bg-success-500/10 text-success-700 dark:text-success-500">
                            AUTHORITATIVE
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-2xs">
                          <div className="rounded-md bg-white/60 dark:bg-ink-900/50 p-2">
                            <span className="text-ink-500">Dispatch</span>
                            <p className="font-mono text-ink-800 dark:text-ink-200">
                              {bagLoadingDispatch?.slice(0, 8)}
                            </p>
                          </div>
                          <div className="rounded-md bg-white/60 dark:bg-ink-900/50 p-2">
                            <span className="text-ink-500">Truck</span>
                            <p className="font-medium text-ink-800 dark:text-ink-200">
                              {truck.registration_number}
                            </p>
                          </div>
                          <div className="rounded-md bg-white/60 dark:bg-ink-900/50 p-2">
                            <span className="text-ink-500">Loaded by</span>
                            <p className="font-medium text-ink-800 dark:text-ink-200">
                              {user?.name || 'Authenticated Driver'}
                            </p>
                          </div>
                          <div className="rounded-md bg-white/60 dark:bg-ink-900/50 p-2">
                            <span className="text-ink-500">Loaded at</span>
                            <p className="text-ink-800 dark:text-ink-200">
                              {bagResult.bags[0]?.loaded_at
                                ? new Date(bagResult.bags[0].loaded_at).toLocaleString()
                                : 'Committed'}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-2">
                            Bag identities
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {bagResult.bags.map((bag: any) => (
                              <div
                                key={bag.id}
                                className="rounded-md bg-white/70 dark:bg-ink-900/60 border border-ink-100 dark:border-ink-800 p-2"
                              >
                                <div className="flex justify-between gap-2">
                                  <span className="font-mono text-xs text-ink-800 dark:text-ink-200">
                                    {bag.bag_qr}
                                  </span>
                                  <span className="text-2xs nums text-ink-500">
                                    {Number(bag.weight_kg).toFixed(2)} kg
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <p className="text-2xs text-ink-500">
                          This manifest is committed to custody history and cannot be regenerated from the Driver portal.
                        </p>
                      </div>
                    )}

                    <button
                      onClick={handleLoadBags}
                      disabled={
                        bagLoading ||
                        bagManifestLoading ||
                        bagWeights.length === 0 ||
                        Boolean(bagResult?.bags?.length)
                      }
                      className="btn-primary w-full"
                    >
                      {bagLoading
                        ? <Spinner size="sm" />
                        : bagResult?.bags?.length
                          ? <Icon.Check className="w-4 h-4" />
                          : <Icon.Package className="w-4 h-4" />
                      }
                      {bagLoading
                        ? 'Committing manifest…'
                        : bagManifestLoading
                          ? 'Verifying manifest…'
                          : bagResult?.bags?.length
                            ? 'Manifest locked'
                            : `Commit ${bagWeights.length} bags & generate QR codes`}
                    </button>
                  </>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* ─── MAP ─── */}
        {activeTab === 'map' && (
          <div className="truck-tab-root truck-tab-map space-y-4">
            {/* Destination list with distances */}
            {activeDispatches.length > 0 && (
              <Card padding="none" className="truck-map-destinations overflow-hidden">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                  <SectionHeader
                    eyebrow="Navigation"
                    title="Active destinations"
                    subtitle="Collection points captured by field agents. Start GPS tracking to see live distance + ETA."
                    icon={<Icon.Navigation className="w-4 h-4" />}
                  />
                </div>
                <div className="divide-y divide-ink-100 dark:divide-ink-800">
                  {activeDispatches.filter(d => d.batch_gps_lat && d.batch_gps_lng).map((d, idx) => {
                    const destLat = Number(d.batch_gps_lat);
                    const destLng = Number(d.batch_gps_lng);
                    const tLat = Number(truck.current_lat);
                    const tLng = Number(truck.current_lng);
                    const hasDist = Boolean(tLat && tLng && destLat && destLng);
                    const routeSummary = routeSummaries[d.id];
                    const directDist = hasDist ? (() => {
                      const R = 6371;
                      const toRad = (x: number) => x * Math.PI / 180;
                      const dLat = toRad(destLat - tLat);
                      const dLng = toRad(destLng - tLng);
                      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(tLat)) * Math.cos(toRad(destLat)) * Math.sin(dLng / 2) ** 2;
                      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                    })() : 0;
                    const dist = routeSummary?.distanceKm ?? directDist;
                    const etaMins = routeSummary?.durationSeconds != null ? Math.max(1, Math.round(routeSummary.durationSeconds / 60)) : null;
                    const distanceKind = routeSummary?.mode === 'road' ? 'Road' : 'Direct';
                    return (
                      <div key={d.id} className="p-4 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-900/40 transition-colors">
                        <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-cargo-500 text-white' : 'bg-ink-200 dark:bg-ink-700 text-ink-600 dark:text-ink-300'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-ink-900 dark:text-ink-50 truncate">{d.batch_farmer_name}</p>
                          <p className="text-2xs text-ink-500 dark:text-ink-400 truncate">
                            {d.batch_farmer_village} · {d.batch_est_weight || '?'} kg · {d.status.replace(/_/g, ' ')}
                          </p>
                        </div>
                        {hasDist && (
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold nums text-cargo-600 dark:text-cargo-500">{dist.toFixed(1)} km</p>
                            <p className="text-2xs text-ink-500 dark:text-ink-400">{distanceKind}{etaMins != null ? ` · ~${etaMins} min` : ''}</p>
                          </div>
                        )}
                        <SmartStatus status={d.status} />
                      </div>
                    );
                  })}
                  {activeDispatches.filter(d => !d.batch_gps_lat || !d.batch_gps_lng).length > 0 && (
                    <div className="p-3 text-2xs text-ink-400 dark:text-ink-500 text-center">
                      {activeDispatches.filter(d => !d.batch_gps_lat || !d.batch_gps_lng).length} dispatch(es) have no GPS coordinates — field agent didn't capture location during batch registration
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Map */}
            <Card padding="none" className="truck-live-map-card overflow-hidden">
              <div className="truck-live-map-header p-4 border-b border-ink-200 dark:border-ink-800 flex items-start justify-between">
                <div>
                  <SectionHeader
                    eyebrow="Telemetry"
                    title="Live route map"
                    icon={<Icon.MapPin className="w-4 h-4" />}
                    className="truck-section-header"
                  />
                  <p className="text-sm text-ink-500 dark:text-ink-400 mt-1.5">
                    {truck.current_lat && truck.current_lng
                      ? `Your position: ${Number(truck.current_lat).toFixed(5)}, ${Number(truck.current_lng).toFixed(5)}`
                      : 'No GPS data — start GPS tracking to see your position and route to destinations.'}
                  </p>
                </div>
                {gpsTracking && (
                  <StatusPill tone={onlineStatus ? 'success' : 'warning'} dot>
                    {onlineStatus ? 'Live' : 'Offline'}
                  </StatusPill>
                )}
              </div>
              <div className="h-[60vh] md:h-[65vh]">
                <TruckMap
                  gpsActive={gpsTracking}
                  gpsAccuracy={gpsAccuracy}
                  truckLat={truck.current_lat}
                  truckLng={truck.current_lng}
                  liveLat={liveGpsPos?.lat}
                  liveLng={liveGpsPos?.lng}
                  destinationLat={activeDispatches[0]?.batch_gps_lat || null}
                  destinationLng={activeDispatches[0]?.batch_gps_lng || null}
                  dispatches={activeDispatches as any}
                  onRouteSummaries={setRouteSummaries}
                />
              </div>
            </Card>
          </div>
        )}

        {/* ─── STATS ─── */}
        {activeTab === 'stats' && stats && (
          <div className="truck-tab-root truck-tab-stats space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total deliveries" value={stats.total_deliveries} icon={<Icon.Check className="w-4 h-4" />} accent="cargo" className="truck-performance-metric" />
              <MetricTile label="Today"            value={stats.deliveries_today} icon={<Icon.Calendar className="w-4 h-4" />} className="truck-performance-metric" />
              <MetricTile label="This week"        value={stats.deliveries_this_week} icon={<Icon.Activity className="w-4 h-4" />} className="truck-performance-metric" />
              <MetricTile label="Avg time (min)"   value={Math.round(stats.avg_delivery_time_mins)} icon={<Icon.Clock className="w-4 h-4" />} className="truck-performance-metric" />
            </div>

            <div className="truck-performance-details-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card padding="lg" className="truck-performance-detail truck-performance-completion">
                <SectionHeader
                  title="On-time rate"
                  icon={<Icon.TrendingUp className="w-4 h-4" />}
                  subtitle="Deliveries completed within 2 hours of dispatch."
                />
                <p className="mt-4 text-4xl font-semibold nums tracking-tight text-ink-900 dark:text-ink-50">
                  {stats.on_time_rate.toFixed(1)}<span className="text-2xl text-ink-400 ml-1">%</span>
                </p>
                <div className="mt-4">
                  <ProgressBar
                    value={stats.on_time_rate}
                    tone={stats.on_time_rate >= 80 ? 'success' : stats.on_time_rate >= 60 ? 'warning' : 'danger'}
                    showLabel
                  />
                </div>
              </Card>

              <Card padding="lg" className="truck-performance-detail truck-performance-health">
                <SectionHeader title="Truck health" icon={<Icon.Gauge className="w-4 h-4" />} />
                <div className="mt-4 space-y-3.5">
                  <HealthRow label="Fuel level" value={`${truck.fuel_level_pct}%`} tone={fuelTone === 'danger' ? 'danger' : 'success'} />
                  <HealthRow label="Odometer" value={`${truck.odometer_km.toLocaleString()} km`} />
                  <HealthRow label="Last service" value={truck.last_maintenance_at ? new Date(truck.last_maintenance_at).toLocaleDateString() : 'Never'} />
                  {truck.next_maintenance_odometer && (
                    <HealthRow
                      label="Next service at"
                      value={`${truck.next_maintenance_odometer.toLocaleString()} km`}
                      tone={truck.odometer_km >= truck.next_maintenance_odometer ? 'danger' : 'neutral'}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ─── MAINTENANCE ─── */}
        {activeTab === 'maintenance' && (
          <div className="truck-tab-root truck-tab-maintenance"><MaintenanceTab truckId={truck.id} /></div>
        )}

        {/* ─── v3.2: ROUTE AI (LOGISTICS agent SVC-017) ─── */}
        {activeTab === 'route-ai' && (
          <div className="truck-tab-root truck-tab-route-ai"><RouteAiTab
            truckId={truck.id}
            activeDispatches={activeDispatches}
            truckLat={truck.current_lat}
            truckLng={truck.current_lng}
            liveLat={liveGpsPos?.lat ?? null}
            liveLng={liveGpsPos?.lng ?? null}
            gpsAccuracy={gpsAccuracy}
            onOpenMap={() => setActiveTab('map')}
          /></div>
        )}
      </main>

      {/* ─── Driver-first bottom navigation (mobile): four highest-frequency journeys + complete drawer. ─── */}
      <nav className="truck-mobile-bottom-nav md:hidden" aria-label="Driver primary navigation">
        {[
          { id: 'dispatches', label: 'Trips', icon: <Icon.Package className="w-5 h-5" /> },
          { id: 'bags', label: 'Bags', icon: <Icon.ClipboardCheck className="w-5 h-5" /> },
          { id: 'map', label: 'Map', icon: <Icon.MapPin className="w-5 h-5" /> },
          { id: 'stats', label: 'Stats', icon: <Icon.TrendingUp className="w-5 h-5" /> },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            className={`truck-mobile-bottom-tab ${activeTab === item.id ? 'is-active' : ''}`}
            aria-current={activeTab === item.id ? 'page' : undefined}
            onClick={() => setActiveTab(item.id as any)}
          >
            <span className="truck-mobile-bottom-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
        <button
          type="button"
          className={`truck-mobile-bottom-tab ${mobileDrawerOpen ? 'is-active' : ''}`}
          aria-label="Open all driver tools"
          aria-expanded={mobileDrawerOpen}
          onClick={() => document.querySelector<HTMLButtonElement>('.ed-mobile-workspace-trigger')?.click()}
        >
          <span className="truck-mobile-bottom-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg></span>
          <span>More</span>
        </button>
      </nav>

      {showConfirmModal && selectedDispatch && (
        <DeliveryConfirmation
          dispatch={selectedDispatch}
          onClose={() => { setShowConfirmModal(false); setSelectedDispatch(null); }}
          onConfirmed={() => { setShowConfirmModal(false); setSelectedDispatch(null); fetchAll(); }}
        />
      )}
          <CopilotWidget userRole="driver" />
</div>
  );
};

/* ─── Status chip (header strip) ─── */
const StatusChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: 'success' | 'warning' | 'danger' | 'neutral';
}> = ({ icon, label, value, sub, tone = 'neutral' }) => {
  const toneText = {
    success: 'text-success-700 dark:text-success-500',
    warning: 'text-warning-700 dark:text-warning-500',
    danger:  'text-danger-700  dark:text-danger-500',
    neutral: 'text-ink-900 dark:text-ink-50',
  }[tone];
  return (
    <div className="truck-status-chip flex items-center gap-2.5 p-2.5 rounded-lg bg-ink-0 dark:bg-ink-900 border border-ink-200/60 dark:border-ink-800">
      <span className="shrink-0 w-7 h-7 rounded-md bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-500 dark:text-ink-400">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400 truncate">{label}</p>
        <p className={`text-sm font-semibold nums truncate ${toneText}`}>{value}</p>
        {sub && <p className="text-2xs text-success-600 dark:text-success-500 nums truncate">{sub}</p>}
      </div>
    </div>
  );
};

/* ─── Health row (stats tab) ─── */
const HealthRow: React.FC<{ label: string; value: string; tone?: 'success' | 'warning' | 'danger' | 'neutral' }> = ({ label, value, tone = 'neutral' }) => {
  const toneClass = {
    success: 'text-success-700 dark:text-success-500',
    warning: 'text-warning-700 dark:text-warning-500',
    danger:  'text-danger-700  dark:text-danger-500',
    neutral: 'text-ink-900 dark:text-ink-50',
  }[tone];
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-ink-500 dark:text-ink-400">{label}</span>
      <span className={`text-sm font-semibold nums ${toneClass}`}>{value}</span>
    </div>
  );
};

/* ─── DispatchCard ─── */
const DispatchCard: React.FC<{
  dispatch: DispatchItem;
  onStatusUpdate: (id: string, status: string) => void;
  onConfirmDelivery: (dispatch: DispatchItem) => void;
  updating: boolean;
}> = ({ dispatch, onStatusUpdate, onConfirmDelivery, updating }) => {
  const isDelivered = dispatch.status === 'DELIVERED';
  const isCancelled = dispatch.status === 'CANCELLED';

  const statusAccent: Record<string, string> = {
    ASSIGNED:    'bg-admin-500',
    DISPATCHED:  'bg-analytics-500',
    IN_TRANSIT: 'bg-cargo-500',
    DELIVERED:   'bg-success-500',
    CANCELLED:   'bg-danger-500',
  };

  return (
    <Card padding="none" className={`overflow-hidden ${isDelivered || isCancelled ? 'opacity-80' : ''}`}>
      <div className={`h-0.5 ${statusAccent[dispatch.status] || 'bg-ink-300'}`} />
      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <SmartStatus status={dispatch.status} />
              <span className="text-2xs font-mono text-ink-400 dark:text-ink-500">#{dispatch.id.slice(0, 8)}</span>
            </div>
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{dispatch.batch_farmer_name}</h3>
            <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{dispatch.batch_farmer_village}</p>
          </div>
          <div className="text-right shrink-0">
            {dispatch.batch_calculated_amount != null && (
              <p className="text-base font-semibold nums text-ink-900 dark:text-ink-50">
                {Number(dispatch.batch_calculated_amount).toLocaleString()} <span className="text-xs text-ink-500 dark:text-ink-400">XAF</span>
              </p>
            )}
            <p className="text-2xs text-ink-400 dark:text-ink-500 nums">
              {dispatch.batch_est_weight ? `${dispatch.batch_est_weight} kg est.` : 'Weight pending'}
            </p>
          </div>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 text-xs mb-3">
          <InfoPair icon={<Icon.Phone className="w-3.5 h-3.5" />} label="Phone" value={dispatch.batch_farmer_phone} />
          <InfoPair icon={<Icon.Weight className="w-3.5 h-3.5" />} label="Weight" value={dispatch.batch_actual_weight ? `${dispatch.batch_actual_weight} kg` : 'Pending'} />
          <InfoPair
            icon={<Icon.Award className="w-3.5 h-3.5" />}
            label="Quality"
            value={dispatch.batch_quality_grade ? `Grade ${dispatch.batch_quality_grade}` : 'N/A'}
          />
          <InfoPair icon={<Icon.Clock className="w-3.5 h-3.5" />} label="Assigned" value={new Date(dispatch.assigned_at).toLocaleString()} />
        </div>

        {/* Delivery confirmation banner */}
        {dispatch.delivery_confirmation_status === 'CONFIRMED' && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-success-50 dark:bg-success-500/10 border border-success-500/20 flex items-center gap-2 text-xs text-success-700 dark:text-success-500">
            <Icon.ClipboardCheck className="w-3.5 h-3.5" />
            <span>Delivery confirmed · <strong className="nums">{dispatch.delivery_confirmed_weight} kg</strong></span>
          </div>
        )}

        {/* Actions */}
        {!isDelivered && !isCancelled && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-ink-100 dark:border-ink-800">
            {dispatch.status === 'ASSIGNED' && (
              <button
                onClick={() => onStatusUpdate(dispatch.id, 'DISPATCHED')}
                disabled={updating}
                className="btn-accent text-xs px-4 py-2.5 min-h-[44px] brand-surface-analytics"
              >
                  {updating ? <Spinner size="sm" /> : <Icon.Send className="w-3.5 h-3.5" />}
                  <span>Start trip</span>
              </button>
            )}
            {dispatch.status === 'DISPATCHED' && (
              <button
                onClick={() => onStatusUpdate(dispatch.id, 'IN_TRANSIT')}
                disabled={updating}
                className="btn-accent text-xs px-4 py-2.5 min-h-[44px] brand-surface-cargo"
              >
                {updating ? <Spinner size="sm" /> : <Icon.Navigation className="w-3.5 h-3.5" />}
                <span>Mark in transit</span>
              </button>
            )}
            {dispatch.status === 'IN_TRANSIT' && (
              <button
                onClick={() => onConfirmDelivery(dispatch)}
                disabled={updating}
                className="btn-accent text-xs px-4 py-2.5 min-h-[44px] brand-surface-agrarian"
              >
                <Icon.Check className="w-3.5 h-3.5" />
                <span>Confirm delivery</span>
              </button>
            )}
            <button
              onClick={() => onStatusUpdate(dispatch.id, 'CANCELLED')}
              disabled={updating}
              className="btn-secondary text-xs px-4 py-2.5 min-h-[44px] text-danger-600 dark:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-500/10"
            >
              <Icon.X className="w-3.5 h-3.5" />
              <span>Cancel</span>
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

const InfoPair: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    <span className="shrink-0 mt-0.5 text-ink-400 dark:text-ink-500">{icon}</span>
    <div className="min-w-0">
      <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 truncate">{label}</p>
      <p className="text-xs text-ink-700 dark:text-ink-200 truncate nums">{value}</p>
    </div>
  </div>
);

/* ─── Maintenance Tab ─── */
const MaintenanceTab: React.FC<{ truckId: string }> = ({ truckId }) => {
  const [logs, setLogs] = useState<MaintenanceLog[]>([]);
  const [loading, setLoading] = useState(true);
  const logsPag = usePagination(logs, 10);

  useEffect(() => {
    apiClient.get('/truck-portal/maintenance')
      .then(res => setLogs(res.data.maintenance_logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [truckId]);

  const typeLabels: Record<string, string> = {
    OIL_CHANGE: 'Oil Change',
    TIRE_ROTATION: 'Tire Rotation',
    BRAKE_SERVICE: 'Brake Service',
    ENGINE_REPAIR: 'Engine Repair',
    BODY_REPAIR: 'Body Repair',
    INSPECTION: 'Inspection',
    REGISTRATION: 'Registration',
    INSURANCE: 'Insurance',
    OTHER: 'Other',
  };
  const typeIcons: Record<string, React.FC<any>> = {
    OIL_CHANGE: Icon.Zap,
    TIRE_ROTATION: Icon.Gauge,
    BRAKE_SERVICE: Icon.AlertTriangle,
    ENGINE_REPAIR: Icon.Wrench,
    BODY_REPAIR: Icon.Wrench,
    INSPECTION: Icon.ClipboardCheck,
    REGISTRATION: Icon.Building,
    INSURANCE: Icon.Check,
    OTHER: Icon.Wrench,
  };

  if (loading) {
    return (
      <Card padding="lg">
        <div className="flex items-center justify-center py-6 text-ink-400 dark:text-ink-500">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (logsPag.total === 0) {
    return (
      <Card padding="lg" className="truck-empty-state-card truck-maintenance-empty">
        <EmptyState
          icon={<Icon.Wrench className="w-5 h-5" />}
          title="No maintenance records"
          description="Service entries performed on this truck will appear here."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-2.5">
      {logsPag.paginatedItems.map(log => {
        const Icon2 = typeIcons[log.maintenance_type] || Icon.Wrench;
        return (
          <Card key={log.id} padding="md" className="flex items-start gap-3">
            <span className="shrink-0 w-9 h-9 rounded-lg bg-cargo-50 dark:bg-cargo-500/10 text-cargo-600 dark:text-cargo-500 flex items-center justify-center">
              <Icon2 className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50">{typeLabels[log.maintenance_type] || log.maintenance_type}</h4>
                <span className="text-2xs text-ink-400 dark:text-ink-500 nums">{new Date(log.performed_at).toLocaleDateString()}</span>
              </div>
              {log.description && <p className="text-xs text-ink-500 dark:text-ink-400 mb-2 text-pretty">{log.description}</p>}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-2xs text-ink-500 dark:text-ink-400 nums">
                {log.cost > 0 && <span>Cost: <strong className="text-ink-700 dark:text-ink-200">{log.cost.toLocaleString()} XAF</strong></span>}
                {log.odometer_km != null && <span>Odometer: <strong className="text-ink-700 dark:text-ink-200">{log.odometer_km.toLocaleString()} km</strong></span>}
                {log.performed_by && <span>By: <strong className="text-ink-700 dark:text-ink-200">{log.performed_by}</strong></span>}
                {log.next_maintenance_km != null && <span>Next: <strong className="text-ink-700 dark:text-ink-200">{log.next_maintenance_km.toLocaleString()} km</strong></span>}
              </div>
            </div>
          </Card>
        );
      })}
      {logsPag.totalPages > 1 && (
        <Card padding="none" className="mt-3">
          <Pagination
            page={logsPag.page}
            pageSize={logsPag.pageSize}
            total={logsPag.total}
            onPageChange={logsPag.setPage}
          />
        </Card>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Route AI — deterministic road facts + LOGISTICS SVC-017 advisory layer
// Geographic truth comes from /truck-portal/road-route and /truck-portal/road-matrix.
// AI is advisory only and never mutates dispatch, GPS, route_history or outbox state.
// ════════════════════════════════════════════════════════════════
type RouteAiRoadFact = {
  dispatchId: string;
  mode: 'road' | 'direct' | 'unavailable';
  distanceKm: number | null;
  directDistanceKm: number | null;
  durationSeconds: number | null;
  source: string | null;
  geometryPoints: number;
  error?: string | null;
};

type RouteAiPlan = {
  orderedDispatchIds: string[];
  totalDistanceKm: number;
  totalDurationSeconds: number;
  routableStops: number;
  degradedStops: number;
  algorithm: 'exact' | 'nearest-neighbour';
  generatedAt: string;
};

const routeAiHaversine = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 6371;
  const rad = (v: number) => v * Math.PI / 180;
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatRouteDistance = (km: number | null | undefined): string => {
  if (km == null || !Number.isFinite(Number(km))) return '—';
  const value = Number(km);
  if (value < 1) return `${Math.max(1, Math.round(value * 1000))} m`;
  return `${value.toFixed(1)} km`;
};

const formatRouteDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(Number(seconds))) return 'Unavailable';
  const value = Number(seconds);
  if (value < 60) return '<1 min';
  return `${Math.round(value / 60)} min`;
};

const routeAiOperationalEntries = (value: any): Array<[string, string]> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const allow = /(priority|sequence|fuel|warning|risk|recommend|schedule|loading|dispatch|timing|delay|capacity|weight)/i;
  const deny = /(origin|destination|latitude|longitude|coord|distance|eta|geometry|path|road|village|district|region|location|route)/i;
  return Object.entries(value)
    .filter(([key, v]) => allow.test(key) && !deny.test(key) && v != null && ['string','number','boolean'].includes(typeof v))
    .map(([key, v]) => [key.replace(/_/g, ' '), String(v)]);
};

const RouteAiTab: React.FC<{
  truckId: string;
  activeDispatches: any[];
  truckLat: number | string | null;
  truckLng: number | string | null;
  liveLat?: number | null;
  liveLng?: number | null;
  gpsAccuracy?: number | null;
  onOpenMap: () => void;
}> = ({ truckId, activeDispatches, truckLat, truckLng, liveLat, liveLng, gpsAccuracy, onOpenMap }) => {
  const [loadingDispatchId, setLoadingDispatchId] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);
  const [roadFacts, setRoadFacts] = useState<Record<string, RouteAiRoadFact>>({});
  const [aiResults, setAiResults] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<RouteAiPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planOrigin, setPlanOrigin] = useState<RouteAiOriginSnapshot | null>(null);
  const [planDispatchSignature, setPlanDispatchSignature] = useState('');
  const roadFactOriginsRef = useRef<Record<string, RouteAiOriginSnapshot>>({});

  const originLat = liveLat != null ? Number(liveLat) : Number(truckLat);
  const originLng = liveLng != null ? Number(liveLng) : Number(truckLng);
  const hasOrigin = Number.isFinite(originLat) && Number.isFinite(originLng) && !(originLat === 0 && originLng === 0);
  const currentAccuracyM = Number.isFinite(Number(gpsAccuracy)) && Number(gpsAccuracy) > 0
    ? Number(gpsAccuracy)
    : null;
  const dispatchSignature = activeDispatches
    .map((d: any) => [
      d.id,
      d.status,
      Number(d.batch_gps_lat || 0).toFixed(6),
      Number(d.batch_gps_lng || 0).toFixed(6),
    ].join(':'))
    .join('|');

  const dispatchById = (id: string) => activeDispatches.find((d: any) => d.id === id);

  const coordsFor = (d: any): [number, number] | null => {
    const lat = Number(d?.batch_gps_lat);
    const lng = Number(d?.batch_gps_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return [lat, lng];
  };

  const fetchRoad = async (fromLat: number, fromLng: number, toLat: number, toLng: number) => {
    try {
      const res = await apiClient.get('/truck-portal/road-route', { params: { fromLat, fromLng, toLat, toLng } });
      const r = res.data?.route;
      const geometry = Array.isArray(r?.geometry) ? r.geometry : [];
      if (!r || geometry.length < 2) throw new Error('Routing provider returned no usable road geometry');
      return {
        mode: 'road' as const,
        distanceKm: Number(r.distance_km),
        directDistanceKm: Number(res.data?.direct_distance_km),
        durationSeconds: Number(r.duration_seconds),
        source: String(r.source || 'routing-provider'),
        geometryPoints: geometry.length,
        error: null,
      };
    } catch (err: any) {
      const direct = Number(err?.response?.data?.direct_distance_km);
      const fallback = Number.isFinite(direct) ? direct : routeAiHaversine(fromLat, fromLng, toLat, toLng);
      return {
        mode: Number.isFinite(direct) ? 'direct' as const : 'unavailable' as const,
        distanceKm: fallback,
        directDistanceKm: fallback,
        durationSeconds: null,
        source: Number.isFinite(direct) ? 'direct-distance-fallback' : null,
        geometryPoints: 0,
        error: err?.response?.data?.error || err?.message || 'Road routing unavailable',
      };
    }
  };

  const optimizePlan = async () => {
    setPlanning(true);
    setPlanError(null);

    try {
      if (!hasOrigin) {
        throw new Error(
          'A valid truck GPS position is required before Route AI can optimize road travel.'
        );
      }

      const stops = activeDispatches
        .map((d: any) => ({ d, c: coordsFor(d) }))
        .filter((x: any) => x.c) as Array<{
          d: any;
          c: [number, number];
        }>;

      if (!stops.length) {
        throw new Error(
          'No active dispatch has valid destination GPS coordinates.'
        );
      }

      const factOrigin: RouteAiOriginSnapshot = {
        lat: originLat,
        lng: originLng,
        accuracyM: currentAccuracyM,
      };

      // Preserve NOW-008 exactly: one-stop automatic planning/replanning
      // continues through /road-route so geometry remains available.
      if (stops.length === 1) {
        const { d, c } = stops[0];
        const rr = await fetchRoad(
          originLat,
          originLng,
          c[0],
          c[1]
        );

        const fact = {
          dispatchId: d.id,
          ...rr,
        } as RouteAiRoadFact;

        roadFactOriginsRef.current[d.id] = factOrigin;
        setRoadFacts(prev => ({
          ...prev,
          [d.id]: fact,
        }));

        if (
          fact.mode !== 'road' ||
          fact.distanceKm == null ||
          fact.durationSeconds == null
        ) {
          throw new Error(
            'No complete routable road sequence could be calculated. Direct-distance fallback remains visible.'
          );
        }

        setPlanOrigin(factOrigin);
        setPlanDispatchSignature(dispatchSignature);

        setPlan({
          orderedDispatchIds: [d.id],
          totalDistanceKm: fact.distanceKm,
          totalDurationSeconds: fact.durationSeconds,
          routableStops: 1,
          degradedStops: activeDispatches.length - 1,
          algorithm: 'exact',
          generatedAt: new Date().toISOString(),
        });

        return;
      }

      // NOW-009: multi-stop route optimization uses one bounded OSRM Table
      // request rather than n origin calls plus n(n-1) pairwise route calls.
      const bounded = stops.slice(0, 8);
      const n = bounded.length;

      const matrixPoints: Array<[number, number]> = [
        [originLat, originLng],
        ...bounded.map(({ c }) => c),
      ];

      const matrixRes = await apiClient.get(
        '/truck-portal/road-matrix',
        {
          params: {
            points: JSON.stringify(matrixPoints),
          },
        }
      );

      const roadMatrix = matrixRes.data?.matrix;
      const distances = roadMatrix?.distances_km;
      const durations = roadMatrix?.durations_seconds;
      const expectedSize = n + 1;

      const validShape = (m: any) =>
        Array.isArray(m) &&
        m.length === expectedSize &&
        m.every(
          (row: any) =>
            Array.isArray(row) &&
            row.length === expectedSize
        );

      if (!validShape(distances) || !validShape(durations)) {
        throw new Error(
          'Routing provider returned an invalid road matrix.'
        );
      }

      const source = String(
        roadMatrix?.source || 'routing-matrix-provider'
      );

      const firstLegFacts: Record<
        string,
        RouteAiRoadFact
      > = {};

      for (let i = 0; i < bounded.length; i++) {
        const { d, c } = bounded[i];

        const rawDistance = distances[0][i + 1];
        const rawDuration = durations[0][i + 1];

        const distance =
          rawDistance == null
            ? Number.NaN
            : Number(rawDistance);

        const duration =
          rawDuration == null
            ? Number.NaN
            : Number(rawDuration);

        const directDistance = routeAiHaversine(
          originLat,
          originLng,
          c[0],
          c[1]
        );

        const routable =
          Number.isFinite(distance) &&
          distance >= 0 &&
          Number.isFinite(duration) &&
          duration > 0;

        firstLegFacts[d.id] = {
          dispatchId: d.id,
          mode: routable ? 'road' : 'direct',
          distanceKm: routable
            ? distance
            : directDistance,
          directDistanceKm: directDistance,
          durationSeconds: routable
            ? duration
            : null,
          source: routable
            ? source
            : 'direct-distance-fallback',
          geometryPoints: 0,
          error: routable
            ? null
            : 'Road matrix returned no routable origin-to-stop path',
        };

        roadFactOriginsRef.current[d.id] = factOrigin;
      }

      setRoadFacts(prev => ({
        ...prev,
        ...firstLegFacts,
      }));

      const matrix: Array<
        Array<{
          distanceKm: number;
          durationSeconds: number;
          mode: string;
        } | null>
      > = Array.from(
        { length: n },
        () => Array(n).fill(null)
      );

      for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
          if (i === j) continue;

          const rawDistance = distances[i + 1][j + 1];
          const rawDuration = durations[i + 1][j + 1];

          const distance =
            rawDistance == null
              ? Number.NaN
              : Number(rawDistance);

          const duration =
            rawDuration == null
              ? Number.NaN
              : Number(rawDuration);

          if (
            Number.isFinite(distance) &&
            distance >= 0 &&
            Number.isFinite(duration) &&
            duration > 0
          ) {
            matrix[i][j] = {
              distanceKm: distance,
              durationSeconds: duration,
              mode: 'road',
            };
          }
        }
      }

      const originCosts = bounded.map(
        ({ d }) => firstLegFacts[d.id]
      );

      const costOf = (order: number[]) => {
        let distance = 0;
        let duration = 0;
        let valid = true;

        if (!order.length) {
          return { distance, duration, valid };
        }

        const first = originCosts[order[0]];

        if (
          !first ||
          first.mode !== 'road' ||
          first.durationSeconds == null ||
          first.distanceKm == null
        ) {
          valid = false;
        } else {
          distance += first.distanceKm;
          duration += first.durationSeconds;
        }

        for (let k = 1; k < order.length; k++) {
          const edge =
            matrix[order[k - 1]][order[k]];

          if (
            !edge ||
            edge.mode !== 'road' ||
            !edge.durationSeconds
          ) {
            valid = false;
            break;
          }

          distance += edge.distanceKm;
          duration += edge.durationSeconds;
        }

        return { distance, duration, valid };
      };

      let bestOrder: number[] = [];
      let algorithm: RouteAiPlan['algorithm'] =
        'nearest-neighbour';

      if (bounded.length <= 7) {
        algorithm = 'exact';

        let bestDuration =
          Number.POSITIVE_INFINITY;

        const used =
          Array(bounded.length).fill(false);

        const order: number[] = [];

        const visit = () => {
          if (order.length === bounded.length) {
            const c = costOf(order);

            if (
              c.valid &&
              c.duration < bestDuration
            ) {
              bestDuration = c.duration;
              bestOrder = [...order];
            }

            return;
          }

          for (
            let i = 0;
            i < bounded.length;
            i++
          ) {
            if (used[i]) continue;

            used[i] = true;
            order.push(i);
            visit();
            order.pop();
            used[i] = false;
          }
        };

        visit();
      }

      if (!bestOrder.length) {
        algorithm = 'nearest-neighbour';

        const remaining = new Set(
          bounded.map((_, i) => i)
        );

        let current: number | null = null;

        while (remaining.size) {
          let chosen: number | null = null;
          let best =
            Number.POSITIVE_INFINITY;

          for (const i of remaining) {
            const seconds =
              current == null
                ? originCosts[i]?.durationSeconds
                : matrix[current][i]
                    ?.durationSeconds;

            if (
              seconds != null &&
              seconds > 0 &&
              seconds < best
            ) {
              best = seconds;
              chosen = i;
            }
          }

          if (chosen == null) break;

          bestOrder.push(chosen);
          remaining.delete(chosen);
          current = chosen;
        }
      }

      const totals = costOf(bestOrder);

      const degradedStops =
        activeDispatches.length -
        bestOrder.length;

      if (!bestOrder.length || !totals.valid) {
        throw new Error(
          'No complete routable road sequence could be calculated. Direct-distance fallbacks remain visible per stop.'
        );
      }

      setPlanOrigin(factOrigin);
      setPlanDispatchSignature(
        dispatchSignature
      );

      setPlan({
        orderedDispatchIds:
          bestOrder.map(
            i => bounded[i].d.id
          ),
        totalDistanceKm:
          totals.distance,
        totalDurationSeconds:
          totals.duration,
        routableStops:
          bestOrder.length,
        degradedStops,
        algorithm,
        generatedAt:
          new Date().toISOString(),
      });
    } catch (err: any) {
      setPlan(null);
      setPlanOrigin(null);
      setPlanDispatchSignature('');
      setPlanError(
        err?.response?.data?.error ||
        err?.message ||
        'Route planning failed'
      );
    } finally {
      setPlanning(false);
    }
  };

  // Single-stop auto-planning and material-movement invalidation.
  // Small GPS movement below the uncertainty-aware material threshold leaves the
  // current deterministic plan untouched. Material movement clears stale route
  // facts/advisories first; the next render then recomputes from /road-route.
  useEffect(() => {
    if (!hasOrigin || activeDispatches.length !== 1 || planning) return;
    const only = activeDispatches[0];
    if (!coordsFor(only)) return;

    if (!plan) {
      void optimizePlan();
      return;
    }

    const dispatchChanged =
      Boolean(planDispatchSignature) &&
      planDispatchSignature !== dispatchSignature;

    const movement = planOrigin
      ? routeAiMovementDecision(
          planOrigin,
          { lat: originLat, lng: originLng, accuracyM: currentAccuracyM }
        )
      : null;

    if (!dispatchChanged && !movement?.shouldReplan) return;

    roadFactOriginsRef.current = {};
    setRoadFacts({});
    setAiResults({});
    setErrors({});
    setPlan(null);
    setPlanOrigin(null);
    setPlanDispatchSignature('');

    if (dispatchChanged) {
      setPlanError('Dispatch state or destination changed. Recalculating deterministic road distance and ETA.');
    } else if (movement) {
      setPlanError(
        `GPS moved ${Math.round(movement.movedMeters)} m from the plan origin ` +
        `(material threshold ${Math.round(movement.thresholdMeters)} m). ` +
        'Recalculating deterministic road distance and ETA.'
      );
    }
  }, [
    hasOrigin,
    activeDispatches.length,
    activeDispatches[0]?.id,
    activeDispatches[0]?.status,
    activeDispatches[0]?.batch_gps_lat,
    activeDispatches[0]?.batch_gps_lng,
    dispatchSignature,
    originLat,
    originLng,
    currentAccuracyM,
    planning,
    plan?.generatedAt,
    planOrigin?.lat,
    planOrigin?.lng,
    planOrigin?.accuracyM,
    planDispatchSignature,
  ]);

  const analyzeDispatch = async (dispatch: any) => {
    setLoadingDispatchId(dispatch.id);
    setErrors(prev => ({ ...prev, [dispatch.id]: '' }));
    try {
      if (!hasOrigin) throw new Error('Start GPS or restore a valid last-known truck position before AI analysis.');
      const c = coordsFor(dispatch);
      if (!c) throw new Error('This dispatch has no valid destination GPS coordinates.');

      let fact = roadFacts[dispatch.id];
      const cachedOrigin = roadFactOriginsRef.current[dispatch.id];
      const cachedMovement = cachedOrigin
        ? routeAiMovementDecision(
            cachedOrigin,
            { lat: originLat, lng: originLng, accuracyM: currentAccuracyM }
          )
        : null;
      const cachedFactStale = !cachedOrigin || Boolean(cachedMovement?.shouldReplan);

      if (!fact || fact.mode !== 'road' || cachedFactStale) {
        const rr = await fetchRoad(originLat, originLng, c[0], c[1]);
        fact = { dispatchId: dispatch.id, ...rr };
        roadFactOriginsRef.current[dispatch.id] = {
          lat: originLat,
          lng: originLng,
          accuracyM: currentAccuracyM,
        };
        setRoadFacts(prev => ({ ...prev, [dispatch.id]: fact! }));
      }

      if (!plan && activeDispatches.length === 1 && fact.mode === 'road' && fact.distanceKm != null && fact.durationSeconds != null) {
        setPlanOrigin({
          lat: originLat,
          lng: originLng,
          accuracyM: currentAccuracyM,
        });
        setPlanDispatchSignature(dispatchSignature);
        setPlan({
          orderedDispatchIds: [dispatch.id],
          totalDistanceKm: fact.distanceKm,
          totalDurationSeconds: fact.durationSeconds,
          routableStops: 1,
          degradedStops: 0,
          algorithm: 'exact',
          generatedAt: new Date().toISOString(),
        });
      }

      const position = plan?.orderedDispatchIds.indexOf(dispatch.id) ?? -1;
      const payload = {
        truckId,
        dispatchId: dispatch.id,
        dispatchTruckId: dispatch.truck_id || truckId,
        originLat,
        originLng,
        destLat: c[0],
        destLng: c[1],
        weightKg: Number(dispatch.batch_est_weight || 0),
        farmerName: dispatch.batch_farmer_name || null,
        village: dispatch.batch_farmer_village || null,
        dispatchStatus: dispatch.status,
        deterministicRouteFacts: {
          mode: fact.mode,
          roadDistanceKm: fact.mode === 'road' ? fact.distanceKm : null,
          roadDurationSeconds: fact.mode === 'road' ? fact.durationSeconds : null,
          directDistanceKm: fact.directDistanceKm,
          routingSource: fact.source,
          geometryPoints: fact.geometryPoints,
          authoritativeForDistanceAndEta: fact.mode === 'road',
        },
        deterministicPlan: plan ? {
          recommendedSequence: plan.orderedDispatchIds,
          thisStopPosition: position >= 0 ? position + 1 : null,
          totalRoadDistanceKm: plan.totalDistanceKm,
          totalRoadDurationSeconds: plan.totalDurationSeconds,
          algorithm: plan.algorithm,
        } : null,
        advisoryInstruction: 'Use deterministicRouteFacts and deterministicPlan as authoritative geographic facts. Do not invent road distance, ETA or geometry. Explain operational priority, fuel considerations, dispatch sequencing and warnings only.',
      };

      const res = await apiClient.post('/ai-agents/LOGISTICS/run', payload);
      setAiResults(prev => ({ ...prev, [dispatch.id]: res.data }));
    } catch (err: any) {
      setErrors(prev => ({ ...prev, [dispatch.id]: err?.response?.data?.error || err?.message || 'LOGISTICS agent failed' }));
    } finally {
      setLoadingDispatchId(null);
    }
  };

  const ordered = plan ? plan.orderedDispatchIds.map(dispatchById).filter(Boolean) : [];

  return (
    <div className="truck-route-assist space-y-4">
      <div className="card p-4 truck-route-assist-intro">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="section-eyebrow">Driver intelligence · SVC-017</p>
            <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Route AI</h3>
            <p className="text-xs text-ink-500 dark:text-ink-400 mt-1 max-w-3xl">Deterministic road routing calculates distance and ETA. LOGISTICS AI explains operational priorities and risks without changing dispatch or GPS evidence.</p>
          </div>
          <button type="button" onClick={optimizePlan} disabled={planning || !hasOrigin || activeDispatches.length === 0} className="btn-primary text-xs">{planning ? 'Calculating road plan...' : 'Optimize all active stops'}</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3"><p className="text-2xs text-ink-500">GPS origin</p><p className="text-sm font-semibold">{hasOrigin ? 'Available' : 'Required'}</p></div>
        <div className="card p-3"><p className="text-2xs text-ink-500">Active stops</p><p className="text-sm font-semibold">{activeDispatches.length}</p></div>
        <div className="card p-3"><p className="text-2xs text-ink-500">Road distance</p><p className="text-sm font-semibold">{plan ? formatRouteDistance(plan.totalDistanceKm) : '—'}</p></div>
        <div className="card p-3"><p className="text-2xs text-ink-500">Driving time</p><p className="text-sm font-semibold">{plan ? formatRouteDuration(plan.totalDurationSeconds) : '—'}</p></div>
      </div>

      {planError && <div className="card p-4 border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/20"><p className="text-xs text-amber-800 dark:text-amber-300">{planError}</p></div>}

      {plan && (
        <div className="card p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><p className="section-eyebrow">Deterministic route plan</p><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">Recommended sequence · {plan.algorithm === 'exact' ? 'Exact minimum-time order' : 'Nearest-road-time heuristic'}</p></div>
            <button type="button" className="btn-secondary text-xs" onClick={onOpenMap}>Open Live Map</button>
          </div>
          <div className="space-y-2">
            {ordered.map((d: any, idx: number) => (
              <div key={d.id} className="flex items-center gap-3 p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <div className="w-7 h-7 rounded-full bg-cargo-500 text-white flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{d.batch_farmer_name || `Dispatch ${d.id.slice(0, 8)}`}</p><p className="text-2xs text-ink-500 truncate">{d.batch_farmer_village || 'Destination'} · {d.batch_est_weight || '?'} kg</p></div>
              </div>
            ))}
          </div>
          <p className="text-2xs text-ink-500">
            {plan.routableStops} road-routable stop(s)
            {plan.degradedStops ? ` · ${plan.degradedStops} stop(s) excluded because complete road routing was unavailable` : ''}.
            {' '}Generated {new Date(plan.generatedAt).toLocaleTimeString()}.
            {' '}Single-stop auto-replan gate: {Math.round(routeAiReplanThresholdMeters(planOrigin?.accuracyM ?? null, currentAccuracyM))} m.
          </p>
        </div>
      )}

      {activeDispatches.length === 0 ? (
        <div className="card p-4 text-center truck-empty-state-card"><p className="text-sm font-medium text-ink-200">No active trip</p><p className="text-xs text-ink-500 mt-1">Route guidance becomes available when a dispatch is assigned.</p></div>
      ) : (
        <div className="space-y-3">
          {activeDispatches.map((d: any) => {
            const fact = roadFacts[d.id];
            const ai = aiResults[d.id];
            const out = ai?.output;
            const c = coordsFor(d);
            const seq = plan?.orderedDispatchIds.indexOf(d.id) ?? -1;
            return (
              <div key={d.id} className="card p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><p className="text-sm font-semibold">{seq >= 0 ? `Stop ${seq + 1} · ` : ''}{d.batch_farmer_name || `Dispatch #${d.id?.slice(0, 8)}`}</p><p className="text-2xs text-ink-500">{d.batch_farmer_village || 'Unknown destination'} · {d.batch_est_weight || '?'} kg · {d.status}</p></div>
                  <button type="button" onClick={() => analyzeDispatch(d)} disabled={loadingDispatchId === d.id || !c || !hasOrigin} className="btn-primary text-xs inline-flex items-center gap-1.5">{loadingDispatchId === d.id ? 'Analyzing...' : 'AI advisory'}</button>
                </div>

                {!c && <p className="text-xs text-amber-700 dark:text-amber-400">Destination GPS missing — road optimization is unavailable for this dispatch.</p>}

                {fact && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900"><p className="text-2xs text-ink-500">{fact.mode === 'road' ? 'Road distance' : 'Direct distance'}</p><p className="text-sm font-semibold">{formatRouteDistance(fact.distanceKm)}</p></div>
                    <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900"><p className="text-2xs text-ink-500">Road ETA</p><p className="text-sm font-semibold">{formatRouteDuration(fact.durationSeconds)}</p></div>
                    <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900"><p className="text-2xs text-ink-500">Routing source</p><p className="text-sm font-semibold truncate">{fact.source || 'Unavailable'}</p></div>
                    <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900"><p className="text-2xs text-ink-500">Geometry evidence</p><p className="text-sm font-semibold">{fact.geometryPoints > 2 ? `${fact.geometryPoints} points` : 'No road geometry'}</p></div>
                  </div>
                )}

                {errors[d.id] && <div className="p-3 border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30"><p className="text-xs text-red-700 dark:text-red-400">{errors[d.id]}</p></div>}

                {ai?.success && out && (
                  <div className="space-y-3 border-t border-ink-200 dark:border-ink-800 pt-3">
                    <div className="flex items-center justify-between gap-2"><p className="text-sm font-semibold text-green-700 dark:text-green-400">LOGISTICS advisory</p><span className="text-2xs text-ink-500">Model: {ai.model || 'unknown'}</span></div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                        <p className="text-2xs text-ink-500">Fuel estimate</p>
                        {out.fuel_status?.calibrated && out.fuel_status?.estimate_liters != null ? (
                          <>
                            <p className="text-sm font-semibold">{Number(out.fuel_status.estimate_liters).toFixed(2)} L</p>
                            <p className="text-2xs text-ink-500 mt-1">
                              Deterministic profile v{out.fuel_status?.provenance?.profile_version ?? '?'}
                              {out.fuel_status?.provenance?.consumption_l_per_100km != null
                                ? ` · ${Number(out.fuel_status.provenance.consumption_l_per_100km).toFixed(2)} L/100 km`
                                : ''}
                            </p>
                            <p className="text-2xs text-ink-500 mt-1">
                              Calibration evidence: {out.fuel_status?.provenance?.calibration_evidence_ref || 'Verified profile'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-semibold">Not calibrated</p>
                            <p className="text-2xs text-ink-500 mt-1">A deterministic vehicle consumption profile is required before fuel quantity can be shown.</p>
                          </>
                        )}
                      </div>
                      {out.confidence != null && <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900"><p className="text-2xs text-ink-500">AI confidence</p><p className="text-sm font-semibold">{out.confidence}%</p></div>}
                      <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900"><p className="text-2xs text-ink-500">Geographic authority</p><p className="text-sm font-semibold">Road engine</p></div>
                    </div>
                    {(() => {
                      const advisory = routeAiOperationalEntries(out.route_optimization);
                      return advisory.length > 0 ? (
                        <div>
                          <p className="text-2xs text-ink-500 mb-1">Operational recommendation</p>
                          <div className="space-y-1">
                            {advisory.map(([key, value]) => (
                              <p key={key} className="text-xs text-ink-700 dark:text-ink-300">
                                <span className="font-medium capitalize">{key}:</span> {value}
                              </p>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-ink-500 dark:text-ink-400">
                          AI geographic narrative is intentionally hidden. Deterministic road facts above remain the only displayed source for location, distance, ETA and geometry.
                        </p>
                      );
                    })()}
                    <p className="text-2xs text-ink-500">AI advice is non-authoritative. Road distance/ETA above come from deterministic routing and dispatch/GPS state remains controlled by authorized workflows.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


export default TruckPortal;
